import {Component, Inject, Input, OnInit} from "@angular/core";
import { fgURL } from "site";
import * as moment from "moment";
import { Aircraft } from "../aircraft";
import { ConnectedCockpitRegistration } from "../connected-cockpit-registration";
import { Device } from "../device";
import { DeviceAvdbType, DeviceAvdbTypeStatus } from "../device-avdb-type";
import { FeatureFlagService } from "fly-common/feature-flag.service";
import { PermissionUtil } from "../shared/permission-util.service";
import {forkJoin} from "../../Rx-angular-stub";

@Component({
	selector: "hercules-device-row",
	template: `
		<div class="devicerow"  [routerLink]="deviceLink">
			<div class="devicerow_content hercules_content">
				<div class="devicerow_details hercules_details">
					<image-container class="devicerow_image" *ngIf="device.model"
						 [width]="100"
						 [height]="80"
						 [src]="device.model.imageURL"
						 [alt]="device.model.displayName">
					</image-container>

					<div class="devicerow_text herculesrow_text">
						<h3>
							<a [routerLink]="deviceLink"
							   (click)="$event.stopPropagation()">{{device.model.name}}</a>
						</h3>
					</div>
				</div>

				<div *ngIf="isHerculesConnectivityEnabled" class="hercules-ccd-download_details">
					<div *ngIf="hasActiveSubscription" class="ccdDownloadStatusIconClass">
						<ng-container [ngSwitch]="ccdDownloadStatusIcon">
							<i *ngSwitchCase="'check'" [ngClass]="[ccdDownloadStatusTextClass, 'fa fa-check-circle']"></i>
							<i *ngSwitchCase="'exclamation'" [ngClass]="[ccdDownloadStatusTextClass, 'fa fa-exclamation-circle']"></i>
							<i *ngSwitchCase="'question'" [ngClass]="[ccdDownloadStatusTextClass, 'fa fa-question-circle']"></i>
						</ng-container>
						<span class="ccd-download-text">{{ ccdDownloadStatus }}</span>
					</div>
					<a class="button"
					   [href]="connectedFeaturesManagementUrl"
					   (click)="$event.stopPropagation()">
						Manage Connected Features
					</a>
				</div>

				<div *ngIf="device.firstUnredeemedBundle && (aircraft == null || hasAvdbPurchasePermission) && !this.isDealerCustomerManagementFlow; else noBundle">
					<a
						[routerLink]="device.redeemBundleLink(device.firstUnredeemedBundle.bundle)"
						(click)="$event.stopPropagation()"
						class="button button-primary">
						Required Action: Redeem {{device.firstUnredeemedBundle.kind}}
					</a>
				</div>

				<ng-template #noBundle>
					<div *ngIf="showAvdbStatus" [ngClass]="['devicerow_status', avdbStatusClass]">
						<ng-container [ngSwitch]="avdbStatusIcon">
							<i *ngSwitchCase="'check'" [ngClass]="'fa fa-check-circle'"></i>
							<i *ngSwitchCase="'exclamation'" [ngClass]="'fa fa-exclamation-circle'"></i>
						</ng-container>
						<span *ngIf="numUpdates > 0">{{ numUpdates }}</span>
						{{ avdbStatus }}
						<p *ngIf="showLatestUpdate" class="center_text">{{ lastUpdatedDate }}</p>
						<p *ngIf="nextAvdbUpdate != null">{{ nextAvdbUpdate }}</p>
						<a *ngIf="device.purchasedButNotInstalledAvdbs.length > 0 && !isDealerCustomerManagementFlow"
						   [routerLink]="['/update-databases', {deviceID: device.id}]"
						   (click)="$event.stopPropagation()"
						   class="devicerow_updatebutton button button-primary">
							Install Updates
						</a>
					</div>
				</ng-template>
			</div>

			<div class="devicerow_rightarrow">
				<i class="fa fa-angle-right fa-2x"></i>
			</div>
		</div>
	`,
})
export class HerculesDeviceRowComponent implements OnInit {
	@Input() aircraft: Aircraft;
	@Input() connectedCockpitRegistration: ConnectedCockpitRegistration;
	@Input() device: Device;
	@Input() customerGuid: string;
	@Input() hasActiveSubscription: boolean;
	@Input() isDealerCustomerManagementFlow: boolean;

	isHerculesConnectivityEnabled: boolean;
	hasAvdbPurchasePermission: boolean = false;
	hasAvdbDownloadPermission: boolean = false;
	numUpdates: number = 0;
	isDealerUser: boolean;

	ccdDownloadStatusIconClass: string = 'connected-cockpit_status-centered';
	ccdDownloadStatusTextClass: string = 'connected-cockpit_status-default';
	ccdDownloadStatusIcon: 'check' | 'exclamation' | 'question' | null = null;
	ccdDownloadStatus: string;

	showAvdbStatus: boolean = true;
	showLatestUpdate: boolean = false;
	avdbStatusClass: string = "devicerow_status-notinstalled";
	avdbStatus: string;
	nextAvdbUpdate: string;
	avdbStatusIcon: 'check' | 'exclamation' | null = null;

	connectedFeaturesManagementUrl: string = null;

	constructor(
		private featureFlagService: FeatureFlagService,
		private permissionUtil: PermissionUtil,
		@Inject("isDealerUser") isDealerUser: boolean
	) {
		this.isDealerUser = isDealerUser;
	}

	ngOnInit() {
		this.getCcdDownloadStatus();

		forkJoin([
			this.permissionUtil.hasAvdbPurchasePermission(this.aircraft),
			this.permissionUtil.hasAvdbDownloadPermission(this.aircraft),
			this.featureFlagService.isHerculesConnectivityEnabled()
		]).subscribe(([avdbPurchasePermission, avdbDownloadPermission, herculesEnabled]) => {
			this.hasAvdbPurchasePermission = avdbPurchasePermission;
			this.hasAvdbDownloadPermission = avdbDownloadPermission;
			this.isHerculesConnectivityEnabled = herculesEnabled;
		});

		this.connectedFeaturesManagementUrl = `${fgURL}aircraft/${this.device.aircraftUuid}/devices/${this.device.systemUuid}/connectivity-features`;
	}

	ngOnChanges() {
		this.getAvdbStatus(this.device.avdbStatus);
	}

	get deviceLink() {
		if (this.isDealerCustomerManagementFlow) {
			return ["/customer/", this.customerGuid, "device", this.device.id];
		} else {
			return ["/details/", this.device.id];
		}
	}

	get lastUpdatedDate(): string {
		const installedDates = this.device.avdbTypes.filter(avdb => avdb.installedIssue != null && avdb.installedIssue.installedAt != null)
			.map(function(avdb) {
				return moment(avdb.installedIssue.installedAt);
			});
		return moment.max(installedDates).format('DD-MMM-YYYY h:mm:ss a');
	}

	private getCcdDownloadStatus() {
		const ccdAvdbStatus = this.aircraft.connectedCockpitDeviceAvdbStatus;
		switch (ccdAvdbStatus) {
			case DeviceAvdbTypeStatus.NOT_INSTALLED:
				this.ccdDownloadStatusTextClass = 'connected-cockpit_status-default';
				this.ccdDownloadStatusIconClass = 'connected-cockpit_status-centered';
				this.ccdDownloadStatusIcon = 'exclamation';
				this.ccdDownloadStatus = this.aircraft.doesNotOwnCurrentCycle ?
					this.isDealerCustomerManagementFlow ?
						'The customer will need to have purchased, or buy databases for this aircraft if the customer wishes to perform database updates.' :
						`You will need to <a href="/fly-garmin/devices/database-pricing;aircraftID=${this.aircraft.id}">buy databases</a> for this aircraft for the device to perform database updates.`
					: 'No Database Information';
				break;
			case DeviceAvdbTypeStatus.LATEST:
				this.ccdDownloadStatusTextClass = 'connected-cockpit_status-latest';
				this.ccdDownloadStatusIconClass = 'connected-cockpit_status-top';
				this.ccdDownloadStatusIcon = 'check';
				this.ccdDownloadStatus = 'Database(s) Downloaded';
				break;
			case DeviceAvdbTypeStatus.OUTDATED:
				this.ccdDownloadStatusTextClass = 'connected-cockpit_status-outdated';
				this.ccdDownloadStatusIconClass = 'connected-cockpit_status-centered';
				this.ccdDownloadStatusIcon = 'exclamation';
				this.ccdDownloadStatus = 'Automatic Database Download Failed';
				break;
			case DeviceAvdbTypeStatus.EXPIRED:
				this.ccdDownloadStatusTextClass = 'connected-cockpit_status-expired';
				this.ccdDownloadStatusIconClass = 'connected-cockpit_status-centered';
				this.ccdDownloadStatusIcon = 'exclamation';
				this.ccdDownloadStatus = 'Automatic Database Download Failed';
				break;
			default:
				this.ccdDownloadStatusTextClass = 'connected-cockpit_status-default';
				this.ccdDownloadStatusIconClass = 'connected-cockpit_status-centered';
				this.ccdDownloadStatusIcon = 'question';
				this.ccdDownloadStatus = 'No Database Information';
		}
	}

	private getAvdbStatus(avdbStatus: string): void {
		let nextUpdate;
		if (this.device.nextExpectedAvdbAvailability) {
			nextUpdate = moment(this.device.nextExpectedAvdbAvailability).fromNow();
		}

		switch (avdbStatus) {
			case DeviceAvdbTypeStatus.NOT_INSTALLED:
				this.numUpdates = this.device.purchasedButNotInstalledAvdbs.length;

				if (this.numUpdates > 0) {
					this.avdbStatusClass = "devicerow_status-notinstalled";
					this.avdbStatus = "Ready to Install";
					this.nextAvdbUpdate = (nextUpdate != null) ? `Next update available ${nextUpdate}.` : null;
					this.avdbStatusIcon = null;
				}
				break;
			case DeviceAvdbTypeStatus.LATEST:
				this.numUpdates = 0;
				this.showLatestUpdate = true;

				this.avdbStatusClass = "devicerow_status-latest";
				this.avdbStatus = "Database(s) Updated On";
				this.nextAvdbUpdate = (nextUpdate != null) ? `Next update available ${nextUpdate}.` : null;
				this.avdbStatusIcon = 'check';
				break;
			case DeviceAvdbTypeStatus.OUTDATED:
				this.numUpdates = this.getDatabasesByStatus(DeviceAvdbTypeStatus.OUTDATED).length;

				this.avdbStatusClass = "devicerow_status-outdated";
				this.avdbStatus = (this.numUpdates === 1) ? "Update Available" : "Updates Available";
				this.nextAvdbUpdate = (nextUpdate != null) ? `Next update available ${nextUpdate}.` : null;
				this.avdbStatusIcon = null;
				break;
			case DeviceAvdbTypeStatus.EXPIRED:
				const expiredAvdbs = this.getDatabasesByStatus(DeviceAvdbTypeStatus.EXPIRED);
				this.numUpdates = expiredAvdbs.length;

				this.avdbStatusClass = "devicerow_status-expired";
				this.avdbStatus = (this.numUpdates === 1) ? "Update Required" : "Updates Required";
				this.nextAvdbUpdate = `${expiredAvdbs.length} expired ${this.getDurationFromInvalidAt(expiredAvdbs)}.`;
				this.avdbStatusIcon = null;
				break;
			default:
				this.showAvdbStatus = false;
				this.avdbStatusClass = "devicerow_status-notinstalled";
		}
	}

	private getDatabasesByStatus(avdbStatus: DeviceAvdbTypeStatus): DeviceAvdbType[] {
		return this.device.avdbTypes.filter(avdbType => avdbType.status === avdbStatus);
	}

	private getDurationFromInvalidAt(avdbs: Array<DeviceAvdbType>): string {
		if(!avdbs || avdbs.length == 0){
			return null;
		}
		const allInvalidAts = avdbs.filter(avdb => avdb.installedIssue.invalidAt)
			.map(function(avdb) {
				return moment(avdb.installedIssue.invalidAt);
			});
		const earliestInvalidAt = moment.min(allInvalidAts);

		if (earliestInvalidAt) {
			return earliestInvalidAt.fromNow();
		}
		else {
			return null;
		}
	}
}
